<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
        <h4 class="mb-0">
          {{ $t('ledger_ct_title') }}
        </h4>
        <b-button
          v-if="canEdit"
          variant="primary"
          class="rounded-pill"
          :to="{ name: 'ledger-contracts-new' }"
        >
          {{ $t('ledger_ct_new') }}
        </b-button>
      </div>
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-input
            v-model="filter.keyword"
            :placeholder="$t('ledger_search')"
            @keyup.enter="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-form-select
            v-model="filter.status"
            :options="statusOptions"
            @change="reload"
          />
        </b-col>
        <b-col
          v-if="canCompany"
          cols="12"
          md="3"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="filter.company"
            @change="reload"
          >
            {{ $t('ledger_rep_company_toggle') }}
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-button
            variant="outline-primary"
            class="rounded-pill"
            @click="reload"
          >
            {{ $t('ledger_assets_load') }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-muted mt-1"
      >
        {{ $t('ledger_ct_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block mt-1">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(contract_number)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.contract_number }}
              </b-link>
            </template>
            <template #cell(project)="data">
              {{ projectLabel(data.item) }}
            </template>
            <template #cell(customer)="data">
              {{ customerName(data.item) }}
            </template>
            <template #cell(contract_value)="data">
              <span class="ledger-num">{{ money(data.item.contract_value) }}</span>
            </template>
            <template #cell(percent)="data">
              <b-progress
                :value="percentValue(data.item)"
                :max="100"
                class="mb-25"
              />
              <span class="ledger-num small">{{ percentLabel(data.item) }}</span>
            </template>
            <template #cell(billed)="data">
              <span class="ledger-num">{{ money(position(data.item).billed_to_date) }}</span>
            </template>
            <template #cell(retention)="data">
              <span class="ledger-num">{{ money(position(data.item).retention_held) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
              <b-badge
                v-if="isLoss(data.item)"
                variant="danger"
                class="ml-50"
              >
                {{ $t('ledger_ct_loss') }}
              </b-badge>
            </template>
          </b-table>
        </div>
        <div class="d-md-none mt-1">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-ct-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.contract_number }}</strong>
              <b-badge :variant="statusVariant(item.status)">
                {{ enumLabel(item.status, $i18n.locale) }}
              </b-badge>
            </div>
            <div>{{ projectLabel(item) }}</div>
            <div class="ledger-num">
              {{ money(item.contract_value) }}
            </div>
            <b-progress
              :value="percentValue(item)"
              :max="100"
            />
            <b-badge
              v-if="isLoss(item)"
              variant="danger"
            >
              {{ $t('ledger_ct_loss') }}
            </b-badge>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="filter.page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || filter.per_page"
          @change="onPage"
        />
      </div>
    </b-card>
    <div
      v-if="rows.length"
      class="ledger-ct-sticky d-md-none"
    >
      <span>{{ $t('ledger_ct_value') }}</span>
      <strong class="ledger-num">{{ money(valueSum) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BFormCheckbox, BButton, BTable, BBadge, BLink, BSpinner, BPagination, BProgress,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getContracts } from '@/api/accounting/contracts'
import {
  listFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, apiErrorMessage,
} from '@/utils/ledger'

const STATUS_VARIANT = {
  active: 'success',
  handed_over: 'info',
  closed: 'dark',
}

export default {
  name: 'ContractList',
  components: {
    BCard, BRow, BCol, BFormInput, BFormSelect, BFormCheckbox, BButton, BTable, BBadge, BLink, BSpinner, BPagination, BProgress,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      filter: {
        keyword: '',
        status: '',
        company: false,
        page: 1,
        per_page: 15,
      },
    }
  },
  computed: {
    canEdit() {
      return this.hasPermission('edit contracts')
    },
    canCompany() {
      return this.hasPermission('view company_reports')
    },
    statusOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: 'active', text: this.$t('ledger_ct_status_active') },
        { value: 'handed_over', text: this.$t('ledger_ct_status_handed') },
        { value: 'closed', text: this.$t('ledger_ct_status_closed') },
      ]
    },
    fields() {
      return [
        { key: 'contract_number', label: this.$t('ledger_ct_number') },
        { key: 'project', label: this.$t('ledger_payroll_project') },
        { key: 'customer', label: this.$t('ledger_inv_customer') },
        { key: 'contract_value', label: this.$t('ledger_ct_value') },
        { key: 'percent', label: this.$t('ledger_ct_percent') },
        { key: 'billed', label: this.$t('ledger_ct_billed') },
        { key: 'retention', label: this.$t('ledger_ct_retention_held') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
    valueSum() {
      return roundMoney(this.rows.reduce((sum, row) => sum + (Number(row.contract_value) || 0), 0))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    position(row) {
      return (row && row.position) || {}
    },
    percentValue(row) {
      const n = Number(this.position(row).percent_complete)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.min(100, n * 100))
    },
    percentLabel(row) {
      const n = Number(this.position(row).percent_complete)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    isLoss(row) {
      return Number(row.expected_profit) < 0
    },
    statusVariant(status) {
      return STATUS_VARIANT[enumValue(status)] || 'secondary'
    },
    projectLabel(row) {
      const project = row.project
      if (!project) return '—'
      return project.project_number ? `${project.project_number} — ${project.name || ''}` : (project.name || '—')
    },
    customerName(row) {
      const project = row.project || {}
      const customer = project.customer || row.customer
      return (customer && customer.name) || '—'
    },
    showRoute(row) {
      return { name: 'ledger-contracts-view', params: { id: row.id } }
    },
    reload() {
      this.filter.page = 1
      this.load()
    },
    onPage(page) {
      this.filter.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.filter.page, per_page: this.filter.per_page }
        if (this.filter.keyword) params.keyword = this.filter.keyword.trim()
        if (this.filter.status) params.status = this.filter.status
        if (this.filter.company && this.canCompany) params.scope = 'company'
        const res = await getContracts(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-ct-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-ct-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
